/**
 * Slide Navigation Hook
 * Manages keyboard navigation, slide state, and direction tracking
 */

import { useState, useEffect, useCallback } from 'react';

interface UseSlideNavigationOptions {
  totalSlides: number;
  initialSlide?: number;
  enabled?: boolean;
}

interface UseSlideNavigationReturn {
  currentSlide: number;
  direction: number;
  goToSlide: (index: number) => void;
  goNext: () => void;
  goPrevious: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function useSlideNavigation({
  totalSlides,
  initialSlide = 0,
  enabled = true,
}: UseSlideNavigationOptions): UseSlideNavigationReturn {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [direction, setDirection] = useState(0);

  const goToSlide = useCallback((index: number) => {
    if (index < 0 || index >= totalSlides) return;
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide, totalSlides]);

  const goNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  const goPrevious = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          goPrevious();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, goNext, goPrevious]);

  return {
    currentSlide,
    direction,
    goToSlide,
    goNext,
    goPrevious,
    isFirst: currentSlide === 0,
    isLast: currentSlide === totalSlides - 1,
  };
}
